import type { Type } from '@angular/core';

/** Id of the base look every release falls back to. */
export const DEFAULT_WHATS_NEW_EVENT = 'default';

/**
 * Visual identity of a What's New event (Halloween, Christmas, Valentine's, an anniversary...).
 * A release opts in with `FuryRelease.event`; registering a new event needs no change to the
 * modal or the services.
 */
export interface WhatsNewEventDef {
  id: string;
  /** Custom properties set on the modal root (e.g. `--wn-accent`); the modal's CSS reads them. */
  cssVars: Readonly<Record<string, string>>;
  /** Replaces the intro title block; receives no inputs. Resolved lazily so seasonal code stays out of the main bundle. */
  introComponent?: () => Promise<Type<unknown>>;
  /** Rendered behind the card's content (particles, ornaments); receives no inputs. */
  decorationComponent?: () => Promise<Type<unknown>>;
}

/** Registered events: the base look plus the seasonal ones. */
export const WHATS_NEW_EVENTS: Readonly<Record<string, WhatsNewEventDef>> = {
  [DEFAULT_WHATS_NEW_EVENT]: {
    id: DEFAULT_WHATS_NEW_EVENT,
    cssVars: {},
  },
  halloween: {
    id: 'halloween',
    cssVars: { '--wn-accent': '#f97316' },
    introComponent: () =>
      import('./events/halloween/halloween-intro.component').then((m) => m.NfHalloweenIntroComponent),
    decorationComponent: () =>
      import('./events/halloween/halloween-decoration.component').then(
        (m) => m.NfHalloweenDecorationComponent,
      ),
  },
};

/** Resolves a release's event, falling back to the default look when absent or unregistered. */
export function resolveWhatsNewEvent(
  eventId: string | undefined,
  registry: Readonly<Record<string, WhatsNewEventDef>> = WHATS_NEW_EVENTS,
  onUnknown: (id: string) => void = (id) =>
    console.warn(`[WhatsNew] Unregistered event "${id}", using the default look.`),
): WhatsNewEventDef {
  if (!eventId) return registry[DEFAULT_WHATS_NEW_EVENT];
  const found = registry[eventId];
  if (found) return found;
  onUnknown(eventId);
  return registry[DEFAULT_WHATS_NEW_EVENT];
}
